import { ExternalLink } from "lucide-react";

import { Badge } from "@/components/_atoms/badge";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { DatatableCellTitle } from "@/domain/view/components/datatable/datatable-template-cells/datatable-cell-title";
import { DatatableColumnHeaderCell } from "@/domain/view/components/datatable/datatable-template-cells/datatable-column-header-cell";
import { DatatableColumn, isRawRow, TSectionData } from "@/domain/view/components/datatable/datatable-types";
import { useWorkspaceTerminology } from "@/domain/workspace/hooks/use-workspace-terminology";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { compareNaturalText } from "@/lib/compare-natural-text";
import { ColumnDef } from "@tanstack/react-table";

import { buildEntityHref } from "../../lib/build-entity-href";
import type { DataRequestEntity } from "../../models/data-request.model";

const COLUMN_ID_ENTITY_TITLE = "entityTitle";
const COLUMN_ID_ENTITY_TYPE = "entityType";
const COLUMN_ID_ASSIGNEE = "assignee";
export const COLUMN_ID_ENTITY_STATUS = "status";
const COLUMN_ID_NOT_POSSIBLE = "notPossibleCount";
const COLUMN_ID_OPEN_ENTITY = "openEntity";

const SHARED_COLUMN_PROPS: Pick<
  DatatableColumn<DataRequestEntity>,
  | "getDefaultVisibilityState"
  | "renderDisplayView"
  | "getFilterConfigurationViewType"
  | "renderFilterConfigurationView"
  | "renderAppliedFilterView"
> = {
  getDefaultVisibilityState: () => true,
  renderDisplayView: () => <></>,
  getFilterConfigurationViewType: () => "none",
  renderFilterConfigurationView: () => null,
  renderAppliedFilterView: () => <></>,
};

function createTextHeader(label: string): ColumnDef<TSectionData<DataRequestEntity>>["header"] {
  return ({ column }) => (
    <DatatableColumnHeaderCell column={column} className="pr-1 text-xs font-normal">
      {label}
    </DatatableColumnHeaderCell>
  );
}

export function useDataRequestEntityColumns({ workspaceId }: { workspaceId: string }): DatatableColumn<DataRequestEntity>[] {
  const { t } = useDomainTranslation("data-request");
  const terminology = useWorkspaceTerminology(workspaceId);
  const { currentWorkspace } = useCurrentWorkspaceContext();

  const entityTitleColumn: DatatableColumn<DataRequestEntity> = {
    columnId: COLUMN_ID_ENTITY_TITLE,
    getDisplayValue: () => t("detail.columns.entityTitle"),
    getOrderValue: () => 1,
    getColumnDef: () => ({
      id: COLUMN_ID_ENTITY_TITLE,
      enableHiding: false,
      enableSorting: true,
      enableColumnFilter: false,
      sortingFn: (rowA, rowB) => {
        if (!isRawRow(rowA.original) || !isRawRow(rowB.original)) return 0;
        const a = rowA.original.entityTitle ?? rowA.original.entityId;
        const b = rowB.original.entityTitle ?? rowB.original.entityId;
        return compareNaturalText(a, b);
      },
      header: createTextHeader(t("detail.columns.entityTitle")),
      cell: ({ row }) => {
        if (!isRawRow(row.original)) return null;
        const displayTitle = row.original.entityTitle ?? row.original.entityId;
        return (
          <DatatableCellTitle row={row} className="max-w-120">
            <span className="my-1 max-w-full overflow-hidden text-ellipsis whitespace-nowrap font-medium">
              {displayTitle}
            </span>
          </DatatableCellTitle>
        );
      },
    }),
    ...SHARED_COLUMN_PROPS,
  };

  const entityTypeColumn: DatatableColumn<DataRequestEntity> = {
    columnId: COLUMN_ID_ENTITY_TYPE,
    getDisplayValue: () => t("detail.columns.entityType"),
    getOrderValue: () => 2,
    getColumnDef: () => ({
      id: COLUMN_ID_ENTITY_TYPE,
      enableHiding: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 160,
      header: createTextHeader(t("detail.columns.entityType")),
      cell: ({ row }) => {
        if (!isRawRow(row.original)) return null;
        const label =
          row.original.entityType === "CLIMATE_ACTION"
            ? terminology.climateAction.singular
            : row.original.entityType === "INDICATOR"
              ? t("entityType.INDICATOR")
              : terminology.project.singular;
        return <span className="px-2 text-sm text-muted-foreground">{label}</span>;
      },
    }),
    ...SHARED_COLUMN_PROPS,
  };

  const assigneeColumn: DatatableColumn<DataRequestEntity> = {
    columnId: COLUMN_ID_ASSIGNEE,
    getDisplayValue: () => t("detail.columns.assignee"),
    getOrderValue: () => 3,
    getColumnDef: () => ({
      id: COLUMN_ID_ASSIGNEE,
      enableHiding: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 160,
      header: createTextHeader(t("detail.columns.assignee")),
      cell: ({ row }) => {
        if (!isRawRow(row.original)) return null;
        const assignee = row.original.assignee;
        return (
          <span className="px-2 text-sm text-muted-foreground">
            {assignee?.fullname ?? assignee?.email ?? "—"}
          </span>
        );
      },
    }),
    ...SHARED_COLUMN_PROPS,
  };

  const statusColumn: DatatableColumn<DataRequestEntity> = {
    columnId: COLUMN_ID_ENTITY_STATUS,
    getDisplayValue: () => t("detail.columns.status"),
    getOrderValue: () => 4,
    enableSectioning: true,
    getSectionKey: (data) => data.status,
    getColumnDef: () => ({
      id: COLUMN_ID_ENTITY_STATUS,
      enableHiding: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 120,
      header: createTextHeader(t("detail.columns.status")),
      cell: ({ row }) => {
        if (!isRawRow(row.original)) return null;
        const status = row.original.status;
        return (
          <span className="px-2">
            <Badge variant={status === "COMPLETED" ? "secondary" : "outline"}>
              {t(`entityStatus.${status}`)}
            </Badge>
          </span>
        );
      },
    }),
    ...SHARED_COLUMN_PROPS,
  };

  const notPossibleColumn: DatatableColumn<DataRequestEntity> = {
    columnId: COLUMN_ID_NOT_POSSIBLE,
    getDisplayValue: () => t("detail.columns.notPossibleCount"),
    getOrderValue: () => 5,
    getColumnDef: () => ({
      id: COLUMN_ID_NOT_POSSIBLE,
      enableHiding: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 120,
      header: createTextHeader(t("detail.columns.notPossibleCount")),
      cell: ({ row }) => {
        if (!isRawRow(row.original)) return null;
        const count = row.original.fieldResponses?.filter((fr) => fr.notPossible).length ?? 0;
        return (
          <span className="px-2 text-sm text-muted-foreground">
            {count > 0 ? <span className="text-warning-foreground">{count}</span> : "—"}
          </span>
        );
      },
    }),
    ...SHARED_COLUMN_PROPS,
  };

  const openEntityColumn: DatatableColumn<DataRequestEntity> = {
    columnId: COLUMN_ID_OPEN_ENTITY,
    getDisplayValue: () => "",
    getOrderValue: () => 6,
    getColumnDef: () => ({
      id: COLUMN_ID_OPEN_ENTITY,
      enableHiding: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 40,
      header: () => null,
      cell: ({ row }) => {
        if (!isRawRow(row.original)) return null;
        const entity = row.original;
        const path = buildEntityHref(currentWorkspace.workspaceUrl, entity.entityType, entity.entityId);
        return (
          <button
            type="button"
            aria-label={t("detail.openEntity")}
            data-stop-row-navigation="true"
            className="flex items-center justify-center text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              window.open(path, "_blank", "noopener,noreferrer");
            }}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        );
      },
    }),
    ...SHARED_COLUMN_PROPS,
  };

  return [entityTitleColumn, entityTypeColumn, assigneeColumn, statusColumn, notPossibleColumn, openEntityColumn];
}
